import fr from 'date-fns/locale/fr'
import { useEffect, useState } from 'react'
import DatePicker, { registerLocale } from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { api_domain } from '../../../config/config'
import { get_nested } from '../../../utils/miscellaneous'
import Tooltip from '../Utils/Tooltip'

registerLocale('fr', fr)

function InputContainer(props) {
  const {
    extraClass,
    id,
    label,
    helpMessage,
    placeholder,
    errorMessage,
    icon,
    prefix,
    tooltip,
    type,
    onBlur,
    maxLength,
    input,
    extraProps,
    handleOnchange,
    disabled,
    meta,
    readonly,
    onClick,
    cypress,
  } = props
  const { value, onChange } = input || props
  const [isFocused, setIsFocused] = useState(false)
  const [isVisible, setIsVisible] = useState(input?.type !== 'password')

  let hasError = false

  if (get_nested(['error'], meta) && get_nested(['touched'], meta)) {
    hasError = meta.error
  }

  let borderStyle = `border-greyLight7 hover:border-primaryLight2 ${
    isFocused ? 'shadow-input border-primaryLight2' : ''
  }`

  if (errorMessage || hasError) {
    borderStyle = `border-error ${isFocused ? 'shadow-input-error' : ''}`
  } else if (disabled) {
    borderStyle = 'border-greyLight7 '
  }

  return (
    <div className={extraClass + (hasError ? ' is-invalid-input' : '')}>
      <div className='flex items-center justify-between'>
        {label ? (
          <label className='mb-6 block text-greyLight1 text-xs leading-xs font-medium'>
            {label}
          </label>
        ) : null}
        {maxLength ? (
          <span className={`text-xs leading-xs ml-auto block text-greyLight3`}>
            <span
              className={`${
                value.length >= maxLength - 5 && value.length <= maxLength
                  ? 'text-warning font-medium'
                  : ''
              } ${value.length > maxLength ? 'text-error font-medium' : ''}`}
            >
              {value.length}
            </span>
            /{maxLength}
          </span>
        ) : null}
      </div>
      <div className='flex items-stretch w-full'>
        {prefix ? (
          <label className='rounded-l-8 border border-r-0 border-greyLight7 flex items-center justify-center pl-14 pr-12 py-10 bg-backgroundPrimary text-sm leading-[24px] text-greyLight3'>
            {prefix}
          </label>
        ) : null}
        <label
          htmlFor={id}
          className={`flex relative border w-full items-center py-10 px-14 transition-color duration-200 ${borderStyle} ${
            prefix ? 'rounded-r-8' : 'rounded-8'
          } ${
            disabled
              ? 'bg-greyLight8 cursor-not-allowed'
              : 'bg-backgroundPrimary'
          }`}
        >
          {input && input.type === 'password' && (
            <input className='hidden' type='password' />
          )}
          {input &&
          input.type === 'file' &&
          typeof input.value == 'string' &&
          input.value.length ? (
            <img width={200} height={200} src={api_domain + input.value} />
          ) : null}
          <input
            onFocus={() => {
              input?.onFocus()
              setIsFocused(true)
            }}
            onBlur={() => {
              input?.onBlur()
              onBlur ? onBlur() : null
              setIsFocused(false)
            }}
            id={id}
            data-cy={cypress || input?.name || id}
            onClick={onClick}
            placeholder={placeholder}
            value={value || ''}
            readOnly={readonly}
            onInput={handleOnchange}
            disabled={disabled}
            autoComplete='new-password'
            className={`w-full text-sm leading-[24px] transition-color duration-200 outline-none ${
              disabled
                ? 'text-greyLight5 bg-greyLight8 cursor-not-allowed placeholder:text-greyLight6'
                : 'text-grey bg-backgroundPrimary placeholder:text-greyLight3'
            }`}
            type={
              input
                ? input.type === 'password' && isVisible
                  ? 'text'
                  : input.type
                : 'text'
            }
            {...extraProps}
          />
          {input && input.type === 'password' && (
            <i
              className={
                'absolute bottom-16 right-16 cursor-pointer text-greyLight1 fa-solid ' +
                (isVisible ? 'fa-eye-slash' : 'fa-eye')
              }
              onClick={() => setIsVisible(!isVisible)}
            />
          )}
          {icon ? (
            <i
              className={`${icon} block ml-8 text-grey w-[16px] h-[16px] text-lg flex items-center justify-center`}
            />
          ) : null}
          {errorMessage ? (
            <i className='icon icon-alert-circle block ml-8 text-greyLight3 w-[20px] h-[20px] text-lg flex items-center justify-center text-error' />
          ) : null}
          {tooltip && !errorMessage ? <Tooltip text={tooltip} /> : null}
        </label>
      </div>
      {helpMessage && !errorMessage ? (
        <span className='mt-4 block text-xs leading-xs text-greyLight3'>
          {helpMessage}
        </span>
      ) : null}
      {errorMessage || hasError ? (
        <span className='mt-4 block text-xs leading-xs text-error'>
          {errorMessage || hasError}
        </span>
      ) : null}
    </div>
  )
}

InputContainer.defaultProps = {
  extraClass: '',
  type: 'text',
  value: '',
  disabled: false,
}

export const Input = (props) => {
  function handleOnchange(event) {
    const on_change = props.input ? props.input.onChange : props.onChange
    const newValue = event.target.value
    on_change(newValue)
    event.target.value = newValue
  }

  return <InputContainer handleOnchange={handleOnchange} {...props} />
}

export default Input

export const InputNumber = (props) => {
  const { onChange, value } = props.input || props
  const [displayValue, setDisplayValue] = useState(value)

  useEffect(() => {
    if (value)
      setDisplayValue(value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' '))
  }, [value])

  function handleOnchange(e) {
    let newValue = e.target.value.replace(/\s/g, '')
    onChange(newValue)
    setDisplayValue(newValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' '))
  }

  return (
    <InputContainer
      {...props}
      handleOnchange={handleOnchange}
      extraProps={{ type: 'text', value: displayValue }}
    />
  )
}

export const InputTime = (props) => {
  const { onChange, value } = props.input || props
  const [displayValue, setDisplayValue] = useState(value)

  useEffect(() => {
    setDisplayValue(value.toString().replace(/\B(?=(\d{2})+(?!\d))/g, ':'))
  }, [value])

  function handleOnchange(e) {
    let newValue = e.target.value.replaceAll(':', '').replace(/\D/g, '')
    onChange(newValue)
    setDisplayValue(newValue.toString().replace(/\B(?=(\d{2})+(?!\d))/g, ':'))
  }

  function onBlur() {
    let newValue = value
    if (value.length === 2 || value.length === 1) newValue = value + '00'
    let hours = newValue.slice(0, -2)
    let minutes = newValue.slice(-2)
    if (hours > 23) hours = '23'
    if (minutes > 59) minutes = '59'
    newValue = hours + minutes
    const newValueDisplay = newValue.replace(/\B(?=(\d{2})+(?!\d))/g, ':')
    onChange(newValue)
    setDisplayValue(newValueDisplay)
  }

  return (
    <InputContainer
      {...props}
      handleOnchange={handleOnchange}
      onBlur={onBlur}
      icon='icon-clock'
      extraProps={{ type: 'text', value: displayValue, maxLength: 5 }}
    />
  )
}

export const InputDateTime = (props) => {
  const { onChange, value } = props.input || props
  const [displayValue, setDisplayValue] = useState(value)

  useEffect(() => {
    setDisplayValue(
      value ? value.toString().replace(/\B(?=(\d{2})+(?!\d))/g, ':') : null
    )
  }, [value])

  function handleOnchange(e) {
    let newValue = e.target.value.replaceAll(':', '').replace(/\D/g, '')
    onChange(newValue)
    setDisplayValue(
      newValue
        ? newValue.toString().replace(/\B(?=(\d{2})+(?!\d))/g, ':')
        : null
    )
  }

  function onBlur() {
    let newValue = value
    if (value.length === 2 || value.length === 1) newValue = value + '00'
    let hours = newValue.slice(0, -2)
    let minutes = newValue.slice(-2)
    if (hours > 23) hours = '23'
    if (minutes > 59) minutes = '59'
    newValue = hours + minutes
    const newValueDisplay = newValue.replace(/\B(?=(\d{2})+(?!\d))/g, ':')
    onChange(newValue)
    setDisplayValue(newValueDisplay)
  }

  return (
    <InputContainer
      {...props}
      handleOnchange={handleOnchange}
      onBlur={onBlur}
      icon='icon-clock'
      extraProps={{ type: 'text', value: displayValue, maxLength: 5 }}
    />
  )
}

export const InputDate = (props) => {
  const { onChange, value } = props.input || props
  const [displayValue, setDisplayValue] = useState(value)

  // useEffect(() => {
  //   setDisplayValue(
  //     value ? value.toString().replace(/\B(?=(\d{2})+(?!\d))/g, ':') : null
  //   )
  // }, [value])
  //
  // function handleOnchange(e) {
  //   let newValue = e.target.value.replaceAll(':', '').replace(/\D/g, '')
  //   onChange(newValue)
  //   setDisplayValue(newValue ? newValue.toString().replace(/\B(?=(\d{2})+(?!\d))/g, ':') : null)
  // }
  //
  // function onBlur() {
  //   let newValue = value
  //   const newValueDisplay = newValue.replace(/\B(?=(\d{2})+(?!\d))/g, ':')
  //   onChange(newValue)
  //   setDisplayValue(newValueDisplay)
  // }

  return (
    <div>
      {props.label ? (<label className={"mb-6 block text-greyLight1 text-xs leading-xs font-medium"}>{props.label}</label>) : null}

      <DatePicker
        selected={value}
        onChange={(date) => onChange(date)}
        dateFormat='dd/MM/yyyy'
        className='flex relative bg-backgroundPrimary border w-full items-center py-10 px-14 transition-color duration-200 border-greyLight7 hover:border-primaryLight2 rounded-8'
      />
    </div>
  )
}
